import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import './SuggestedPioneersRow.css';

import placeholderProfile from '../assets/placeholder-profile.png';

const DISPLAY_LIMIT = 10;

function SuggestedPioneersRow({ user, onOpenProfile, onSeeAll }) {
  const navigate = useNavigate();

  const [visibleUsers, setVisibleUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actioning, setActioning] = useState({});

  const poolRef = useRef([]);
  const cursorRef = useRef(DISPLAY_LIMIT);

  useEffect(() => {
    if (!user?.username) return;

    let cancelled = false;

    const fetchSuggestedPioneers = async () => {
      try {
        setLoading(true);

        const res = await api.get('/suggested-pioneers');
        const users = Array.isArray(res.data?.users) ? res.data.users : [];

        const seen = new Set();


const viewerUsername = String(user.username || '')
  .trim()
  .toLowerCase();

const cleanUsers = users.filter((item) => {
  const username = String(item?.username || '')
    .trim()
    .toLowerCase();

  if (!username) return false;

  // Never show the logged-in user.
  if (username === viewerUsername) return false;

  // Home suggestions must only contain people not already followed.
  if (item?.isFollowing === true) return false;

  // Remove duplicates.
  if (seen.has(username)) return false;

  seen.add(username);
  return true;
});



        if (cancelled) return;

        poolRef.current = cleanUsers;
        cursorRef.current = DISPLAY_LIMIT;

        setVisibleUsers(cleanUsers.slice(0, DISPLAY_LIMIT));
      } catch (err) {
        console.error('❌ Failed to fetch suggested pioneers:', err);

        if (!cancelled) {
          poolRef.current = [];
          cursorRef.current = DISPLAY_LIMIT;
          setVisibleUsers([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchSuggestedPioneers();

    return () => {
      cancelled = true;
    };
  }, [user?.username]);

  const formatFollowers = (count = 0) => {
    const n = Number(count) || 0;

    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M followers`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K followers`;
    if (n === 1) return '1 follower';

    return `${n} followers`;
  };

  const replaceCard = (targetUsername) => {
    const normalizedTarget = String(targetUsername || '').trim().toLowerCase();

    let nextUser = null;

    while (cursorRef.current < poolRef.current.length && !nextUser) {
      const candidate = poolRef.current[cursorRef.current];
      cursorRef.current += 1;

      const candidateUsername = String(candidate?.username || '').trim().toLowerCase();

      if (!candidateUsername) continue;
      if (candidateUsername === normalizedTarget) continue;

      nextUser = candidate;
    }

    setVisibleUsers((prev) => {
      const filtered = prev.filter((item) => {
        const itemUsername = String(item?.username || '').trim().toLowerCase();
        return itemUsername !== normalizedTarget;
      });

      if (!nextUser) return filtered;

      const alreadyVisible = filtered.some((item) => {
        return (
          String(item?.username || '').trim().toLowerCase() ===
          String(nextUser?.username || '').trim().toLowerCase()
        );
      });

      if (alreadyVisible) return filtered;

      return [...filtered, nextUser].slice(0, DISPLAY_LIMIT);
    });
  };

  const handleRemove = (e, suggestedUser) => {
    e.stopPropagation();

    if (!suggestedUser?.username) return;

    replaceCard(suggestedUser.username);
  };

  const handleFollow = async (e, suggestedUser) => {
    e.stopPropagation();

    if (!user?.username || !suggestedUser?.username) return;

    const viewerUsername = String(user.username).trim().toLowerCase();
    const targetUsername = String(suggestedUser.username).trim().toLowerCase();

    if (!viewerUsername || !targetUsername || viewerUsername === targetUsername) {
      return;
    }

    setActioning((prev) => ({
      ...prev,
      [targetUsername]: true,
    }));

    try {
      const res = await api.post('/suggested-pioneers/toggle-follow', {
        username: suggestedUser.username,
      });

      const nowFollowing =
        typeof res.data?.nowFollowing === 'boolean'
          ? res.data.nowFollowing
          : true;

      if (nowFollowing) {
        replaceCard(suggestedUser.username);
      }
    } catch (err) {
      console.error('❌ Suggested pioneer follow failed:', err);
    } finally {
      setActioning((prev) => ({
        ...prev,
        [targetUsername]: false,
      }));
    }
  };

  const handleOpenProfile = (suggestedUser) => {
    if (!suggestedUser?.username) return;
    onOpenProfile?.(suggestedUser.username);
  };


const handleSeeAll = () => {
  if (typeof onSeeAll === 'function') {
    onSeeAll();
    return;
  }

  navigate('/discover-people');
};

  const renderHeader = () => (
    <div className="suggested-pioneers-header">
      <h3>Suggested Pioneers</h3>

      <button
        type="button"
        className="suggested-pioneers-see-all"
        onClick={handleSeeAll}
      >
        See all
      </button>
    </div>
  );

  if (loading && visibleUsers.length === 0) {
    return (
      <section className="suggested-pioneers-row">
        {renderHeader()}

        <div className="suggested-pioneers-scroll">
          {Array.from({ length: 4 }).map((_, index) => (
            <div
              className="suggested-pioneer-card suggested-pioneer-card--skeleton"
              key={index}
            >
              <div className="suggested-pioneer-avatar-skeleton" />
              <div className="suggested-pioneer-line-skeleton suggested-pioneer-line-skeleton--wide" />
              <div className="suggested-pioneer-line-skeleton" />
              <div className="suggested-pioneer-button-skeleton" />
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (!visibleUsers.length) return null;

  return (
    <section className="suggested-pioneers-row">
      {renderHeader()}

      <div className="suggested-pioneers-scroll">
        {visibleUsers.map((suggestedUser) => {
          const usernameKey = String(suggestedUser?.username || '').trim().toLowerCase();
          const isBusy = Boolean(actioning[usernameKey]);

          return (
            <article
              className="suggested-pioneer-card"
              key={suggestedUser._id || suggestedUser.username}
              onClick={() => handleOpenProfile(suggestedUser)}
            >
              <button
                type="button"
                className="suggested-pioneer-remove-x"
                onClick={(e) => handleRemove(e, suggestedUser)}
                aria-label="Remove suggestion"
              >
                ×
              </button>

              <img
                src={suggestedUser.profilePic || placeholderProfile}
                alt={suggestedUser.name || suggestedUser.username || 'Suggested Pioneer'}
                className="suggested-pioneer-avatar"
                onError={(e) => {
                  e.currentTarget.src = placeholderProfile;
                }}
              />

<div
  className="suggested-pioneer-name"
  title={suggestedUser.name || suggestedUser.username}
>
  {suggestedUser.name || suggestedUser.username}
</div>

              <div className="suggested-pioneer-followers">
                {formatFollowers(suggestedUser.followersCount)}
              </div>

              <button
                type="button"
                className="suggested-pioneer-follow-btn"
                disabled={isBusy}
                onClick={(e) => handleFollow(e, suggestedUser)}
              >
                {isBusy ? 'Following...' : 'Follow'}
              </button>
            </article>
          );
        })}
      </div>
    </section>
  );
}

export default SuggestedPioneersRow;
